import type { PropsWithChildren, Ref } from "react";
import { createContext } from "react";
import type { FlatList, ScrollView, SectionList } from "react-native";
import type Animated from "react-native-reanimated";

type ScrollComponent =
  | ScrollView
  | Animated.ScrollView
  | FlatList<any>
  | SectionList<any, any>;

export type ScrollRef = React.RefObject<ScrollComponent | null>;

type ScrollContextType = {
  scrollRef: ScrollRef;
};

export const ScrollContext = createContext<ScrollContextType | null>(null);

type Props = {
  scrollRef: ScrollRef;
};

export const mergeRefs =
  <T extends ScrollComponent>(
    ...refs: Array<Ref<T> | undefined | null>
  ): ((instance: T | null) => void) =>
  (instance: T | null) => {
    for (const ref of refs) {
      if (ref == null) continue;

      if (typeof ref === "function") {
        ref(instance);
      } else {
        ref.current = instance;
      }
    }
  };

/**
 * Context provider that allows child components (e.g. pages) that have their own scrollable
 * component (e.g. ScrollView, FlatList, SectionList) to register a ref to it, so it becomes
 * globally accessible.
 */
export const ScrollProvider = ({
  children,
  scrollRef,
}: PropsWithChildren<Props>) => (
  <ScrollContext.Provider value={{ scrollRef }}>
    {children}
  </ScrollContext.Provider>
);
